/**
 * Live Score Notifications - Animated popups for birdies, eagles, hole-in-ones
 */
import React, { useState, useEffect } from 'react';
import { X, Trophy, Star, Zap } from 'lucide-react';

// Individual notification popup
const ScorePopup = ({ notification, onDismiss }) => {
  const { type, player_name, team_name, hole, score } = notification;
  
  const configs = {
    hole_in_one: {
      bg: 'bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600',
      icon: '🎯',
      title: 'HOLE IN ONE!',
      subtitle: 'INCREDIBLE SHOT!',
      animation: 'animate-bounce',
      ring: 'ring-4 ring-purple-400/50'
    },
    eagle: {
      bg: 'bg-gradient-to-r from-amber-500 to-yellow-500',
      icon: '🦅',
      title: 'EAGLE!',
      subtitle: '2 Under Par',
      animation: 'animate-pulse',
      ring: 'ring-2 ring-amber-400/50'
    },
    birdie: {
      bg: 'bg-gradient-to-r from-green-500 to-emerald-500',
      icon: '🐦',
      title: 'BIRDIE!',
      subtitle: '1 Under Par',
      animation: '',
      ring: 'ring-2 ring-green-400/50'
    },
    double_eagle: {
      bg: 'bg-gradient-to-r from-amber-600 via-yellow-400 to-amber-600',
      icon: '🏆',
      title: 'DOUBLE EAGLE!',
      subtitle: 'ALBATROSS - 3 Under Par',
      animation: 'animate-bounce',
      ring: 'ring-4 ring-yellow-400/50'
    }
  };
  
  const config = configs[type] || configs.birdie;
  
  useEffect(() => {
    // Auto-dismiss after 5 seconds
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, [onDismiss]);
  
  return (
    <div className={`${config.bg} ${config.ring} rounded-2xl p-4 shadow-2xl text-white relative overflow-hidden ${config.animation}`}>
      {/* Background sparkles for special shots */}
      {(type === 'hole_in_one' || type === 'double_eagle') && (
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full animate-ping"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${1 + Math.random()}s`
              }}
            />
          ))}
        </div>
      )}
      
      <button 
        onClick={onDismiss}
        className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      
      <div className="flex items-center gap-4 relative">
        {/* Icon */}
        <div className="text-5xl">{config.icon}</div>
        
        {/* Content */}
        <div className="flex-1">
          <p className="text-xs opacity-80 uppercase tracking-wider">{config.subtitle}</p>
          <h3 className="text-2xl font-black">{config.title}</h3>
          <div className="mt-1">
            <p className="font-semibold">{player_name || team_name}</p>
            <p className="text-sm opacity-80">Hole {hole} • Score: {score}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast-style mini notification
const MiniNotification = ({ notification, onDismiss }) => {
  const { type, player_name, hole } = notification;
  
  const icons = {
    hole_in_one: '🎯',
    double_eagle: '🏆',
    eagle: '🦅',
    birdie: '🐦'
  };
  
  const colors = {
    hole_in_one: 'bg-purple-500/90',
    double_eagle: 'bg-amber-500/90',
    eagle: 'bg-amber-500/90',
    birdie: 'bg-green-500/90'
  };
  
  useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [onDismiss]);
  
  return (
    <div className={`${colors[type] || 'bg-green-500/90'} backdrop-blur-sm rounded-full px-4 py-2 text-white shadow-lg flex items-center gap-2 animate-slide-in`}>
      <span className="text-xl">{icons[type] || '🐦'}</span>
      <span className="font-semibold text-sm">
        {player_name} {type.replace('_', ' ')} on #{hole}
      </span>
      <button onClick={onDismiss} className="ml-2 hover:bg-white/20 rounded-full p-1">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

// Main Notifications Container
export default function LiveScoreNotifications({ outingId }) {
  const [notifications, setNotifications] = useState([]);
  const [miniNotifications, setMiniNotifications] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!outingId) return;
    
    // Poll for new score events
    const fetchNotifications = async () => {
      try {
        const res = await fetch(`${API_URL}/api/outings/${outingId}/score-events?since=${Date.now() - 30000}`);
        const data = await res.json();
        
        if (data.success && data.events?.length > 0) {
          // Filter for special scores only
          const specialScores = data.events.filter(e => 
            ['birdie', 'eagle', 'double_eagle', 'hole_in_one'].includes(e.type)
          );
          
          // Big popup for eagles and better
          const bigNotifications = specialScores.filter(e => 
            ['eagle', 'double_eagle', 'hole_in_one'].includes(e.type)
          );
          
          // Mini toast for birdies
          const miniNotifs = specialScores.filter(e => e.type === 'birdie');
          
          if (bigNotifications.length > 0) {
            setNotifications(prev => [...prev, ...bigNotifications]);
          }
          
          if (miniNotifs.length > 0) {
            setMiniNotifications(prev => [...prev, ...miniNotifs]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch score events:', err);
      }
    };
    
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [outingId, API_URL]);

  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const dismissMiniNotification = (id) => {
    setMiniNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Demo function to add test notifications (can be called from console)
  useEffect(() => {
    window.addTestNotification = (type = 'birdie') => {
      const testNotif = {
        id: Date.now(),
        type,
        player_name: 'Test Player',
        team_name: 'Test Team',
        hole: Math.floor(Math.random() * 18) + 1,
        score: type === 'birdie' ? 2 : type === 'eagle' ? 2 : 1
      };
      
      if (type === 'birdie') {
        setMiniNotifications(prev => [...prev, testNotif]);
      } else {
        setNotifications(prev => [...prev, testNotif]);
      }
    };
    
    return () => {
      delete window.addTestNotification;
    };
  }, []);

  return (
    <>
      {/* Big popup notifications - centered */}
      {notifications.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
          <div className="pointer-events-auto">
            <ScorePopup
              notification={notifications[0]}
              onDismiss={() => dismissNotification(notifications[0].id)}
            />
          </div>
        </div>
      )}
      
      {/* Mini toast notifications - top right */}
      <div className="fixed top-20 right-4 z-40 space-y-2">
        {miniNotifications.slice(0, 3).map((notif) => (
          <MiniNotification
            key={notif.id}
            notification={notif}
            onDismiss={() => dismissMiniNotification(notif.id)}
          />
        ))}
      </div>
      
      {/* CSS for animations */}
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
